import React,{useState} from 'react';
import {Link,useLocation} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {posesData,categories} from '../data/poses';

const {FiArrowLeft,FiCopy,FiCheck,FiImage,FiX,FiCheckSquare,FiSquare,FiCamera}=FiIcons;

// Color mapping for categories
const categoryBackgroundColors={
  business: '#0F0F0F',
  senior: '#1C1C1C', 
  couple: '#2E2A2B',
  creative: '#3B2F2F',
  lifestyle: '#4B3B3F',
  fitness: '#5C4B51',
  artistic: '#6D5C5F',
  branding: '#7E6D6F'
};

const getCategoryId=(subject)=> {
  const subjectMap={
    'Business': 'business',
    'Senior': 'senior',
    'Couple': 'couple',
    'Creative': 'creative',
    'Lifestyle': 'lifestyle',
    'Fitness': 'fitness',
    'Artistic': 'artistic',
    'Branding': 'branding'
  };
  return subjectMap[subject] || 'business';
};

function SelectionView() {
  const location=useLocation();
  const [selectedPoses,setSelectedPoses]=React.useState(()=>{
    // Get from localStorage or use the state passed from navigation
    const stored=JSON.parse(localStorage.getItem('selectedPoses') || '[]');
    return location.state?.selectedPoses || stored;
  });
  const [copiedInstructions,setCopiedInstructions]=useState(new Set());
  const [copyingAll,setCopyingAll]=useState(false);
  
  const selectedPoseData=posesData.filter(pose=> selectedPoses.includes(pose.id));

  const getPoseNumber=(poseId)=> {
    const parts=poseId.split('-');
    return parts[1] ? parseInt(parts[1]) : 1;
  };

  const getCategoryName=(subject)=> {
    const category=categories.find(cat=> cat.name===subject);
    return category ? category.name : subject;
  };

  const copySingleInstruction=(poseId)=> {
    const pose=posesData.find(p=> p.id===poseId);
    if (pose) {
      navigator.clipboard.writeText(pose.instruction);
      setCopiedInstructions(prev=> new Set([...prev,poseId]));
      setTimeout(()=> {
        setCopiedInstructions(prev=> {
          const newSet=new Set(prev);
          newSet.delete(poseId);
          return newSet;
        });
      },2000);
    }
  };

  const copyAllInstructions=()=> {
    const allInstructions=selectedPoseData 
      .map(pose=> `${pose.subject} Pose ${getPoseNumber(pose.id)}:\n${pose.instruction}`) 
      .join('\n\n---\n\n');
    navigator.clipboard.writeText(allInstructions);
    setCopyingAll(true);
    setTimeout(()=> setCopyingAll(false),2000);
  };

  const removePoseFromSelection=(poseId)=> {
    const newSelections=selectedPoses.filter(id=> id !==poseId);
    setSelectedPoses(newSelections);
    localStorage.setItem('selectedPoses',JSON.stringify(newSelections));
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('favoritesUpdated'));
  };

  const clearSelection=()=> {
    setSelectedPoses([]);
    localStorage.setItem('selectedPoses',JSON.stringify([]));
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('favoritesUpdated'));
  };

  if (selectedPoses.length===0) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold text-slate-800 mb-4">No poses selected</h2>
        <Link to="/" className="text-brand-primary hover:text-brand-primary-dark">
          Return to home
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <motion.div initial={{y: -20,opacity: 0}} animate={{y: 0,opacity: 1}} transition={{duration: 0.6}} className="mb-8" >
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-800 transition-colors text-sm">
            <SafeIcon icon={FiArrowLeft} className="mr-2" />
            Back to Categories
          </Link>
          <h1 className="text-lg font-bold text-slate-800">Selected Poses</h1>
          <div className="flex items-center gap-4">
            <span className="text-xs font-medium text-slate-600">
              {selectedPoses.length} pose{selectedPoses.length !==1 ? 's' : ''} selected
            </span>
            <motion.button 
              whileHover={{scale: 1.05}} 
              whileTap={{scale: 0.95}} 
              onClick={copyAllInstructions} 
              className={`flex items-center px-4 py-2 rounded-lg transition-all text-sm ${
                copyingAll 
                  ? 'bg-brand-accent/20 text-brand-accent-dark' 
                  : 'bg-brand-primary hover:bg-brand-primary-dark text-white'
              }`} 
            >
              <SafeIcon icon={copyingAll ? FiCheck : FiCopy} className="mr-2" />
              {copyingAll ? 'All Copied!' : 'Copy All'}
            </motion.button>
            <button 
              onClick={clearSelection} 
              className="text-xs text-brand-accent hover:text-brand-accent-dark" 
            >
              Clear
            </button>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <p className="text-sm text-slate-600">Review and copy instructions for your selected poses</p>
        </div>
      </motion.div>

      {/* Selected Poses Grid */}
      <motion.div initial={{y: 20,opacity: 0}} animate={{y: 0,opacity: 1}} transition={{duration: 0.6,delay: 0.2}} className="space-y-4" >
        {selectedPoseData.map((pose,index)=> (
          <motion.div 
            key={pose.id} 
            initial={{y: 20,opacity: 0}} 
            animate={{y: 0,opacity: 1}} 
            transition={{duration: 0.5,delay: index * 0.05}} 
            className="bg-white rounded-xl shadow-lg overflow-hidden" 
          >
            <div className="flex flex-col sm:flex-row">
              {/* Image */}
              <div className="w-full sm:w-48 sm:h-48 aspect-square relative flex-shrink-0" style={{backgroundColor: categoryBackgroundColors[getCategoryId(pose.subject)]}}>
                <img 
                  src={pose.link} 
                  alt={`${getCategoryName(pose.subject)} Pose ${getPoseNumber(pose.id)}`} 
                  className="w-full h-full object-cover" 
                  onError={(e)=> {
                    e.target.style.display='none';
                    e.target.nextSibling.style.display='flex';
                  }} 
                />
                <div style={{display: 'none'}} className="w-full h-full flex items-center justify-center">
                  <SafeIcon icon={FiImage} className="text-2xl text-white/50" />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 p-4 flex flex-col">
                <div className="flex-1">
                  <h3 className="font-bold text-slate-800 mb-2 text-sm">
                    {getCategoryName(pose.subject)} Pose {getPoseNumber(pose.id)}
                  </h3>
                  <p className="text-xs text-slate-600 leading-relaxed line-clamp-3">
                    {pose.instruction}
                  </p>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200 flex-wrap gap-2">
                  <Link 
                    to={`/pose/${pose.id}`} 
                    className="text-xs text-brand-primary hover:text-brand-primary-dark flex-shrink-0"
                  >
                    View Details
                  </Link>
                  <div className="flex items-center gap-2">
                    <motion.button 
                      whileHover={{scale: 1.05}} 
                      whileTap={{scale: 0.95}} 
                      onClick={()=> copySingleInstruction(pose.id)} 
                      className={`flex items-center px-3 py-1 rounded text-xs transition-all flex-shrink-0 ${
                        copiedInstructions.has(pose.id) 
                          ? 'bg-brand-accent/20 text-brand-accent-dark' 
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`} 
                    >
                      <SafeIcon icon={copiedInstructions.has(pose.id) ? FiCheck : FiCopy} className="mr-1 text-xs" />
                      {copiedInstructions.has(pose.id) ? 'Copied' : 'Copy'}
                    </motion.button>
                    <motion.button 
                      whileHover={{scale: 1.05}} 
                      whileTap={{scale: 0.95}} 
                      onClick={()=> removePoseFromSelection(pose.id)} 
                      className="flex items-center px-3 py-1 rounded text-xs bg-red-100 text-red-700 hover:bg-red-200 transition-all flex-shrink-0" 
                    >
                      <SafeIcon icon={FiX} className="mr-1 text-xs" />
                      Remove
                    </motion.button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* All Instructions Section */}
      <motion.div initial={{y: 20,opacity: 0}} animate={{y: 0,opacity: 1}} transition={{duration: 0.6,delay: 0.4}} className="mt-8" >
        <div className="bg-slate-50 rounded-2xl p-8">
          <h2 className="text-xl font-bold text-slate-800 mb-6">All Instructions</h2>
          <div className="space-y-6">
            {selectedPoseData.map((pose)=> (
              <div key={pose.id} className="bg-white rounded-lg p-4">
                <h3 className="font-semibold text-slate-800 mb-2 text-sm">
                  {getCategoryName(pose.subject)} Pose {getPoseNumber(pose.id)}
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  "{pose.instruction}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default SelectionView;